/**
 * External exchange-rate API client (Phase 105, Plan 105-09, D-10).
 *
 * Mirrors `externalLeads.ts` structurally: this module DOES NOT route
 * through `services/api/client.ts` (would clear
 * `localStorage.monksheets_auth_token` on any 401 — internal-only by
 * contract, see `client.ts`'s `handleResponse` comment) and it does NOT
 * reuse the internal rate-fetching function exported from
 * `services/exchangeRate.ts` (that one routes through `client.ts`). We use
 * `fetch()` directly with `credentials:
 * 'include'` so the HttpOnly external session cookie is sent, plus a
 * mandatory `X-Audience: external` header so `authenticateAny` resolves the
 * external session even when an internal session also exists in the same
 * browser (v4.0 Landmine #8).
 *
 * Read-only: no mutation function is exported.
 */
import { ExternalApiError } from './externalAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface ExternalExchangeRatesResponse {
  data: { rates: Record<string, number> };
}

/**
 * Fetch ČNB exchange rates for the external Leads surface.
 * @param date - Date in YYYY-MM-DD format.
 * @returns Map of currency code to rate per 1 unit.
 */
export async function fetchExternalExchangeRates(date: string): Promise<Map<string, number>> {
  const res = await fetch(`${API_BASE_URL}/external/exchange-rates?date=${date}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Audience': 'external',
    },
  });
  if (!res.ok) {
    type ErrorPayload = { error?: string; message?: string };
    const payload = (await res.json().catch(() => ({}))) as ErrorPayload;
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
    });
  }
  const body = (await res.json()) as ExternalExchangeRatesResponse;
  return new Map(Object.entries(body.data.rates));
}
